import { Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { CardSelector } from "@/components/poker/card-selector";
import { CountdownOverlay } from "@/components/poker/countdown-overlay";
import { InviteDialog } from "@/components/poker/invite-dialog";
import { IssueSidebar } from "@/components/poker/issue-sidebar";
import { JoinForm } from "@/components/poker/join-form";
import { PokerTable } from "@/components/poker/poker-table";
import { ResultsPanel } from "@/components/poker/results-panel";
import { RoomHeader } from "@/components/poker/room-header";
import { RoomSkeleton } from "@/components/poker/room-skeleton";
import { AdSlot } from "@/components/ads/ad-slot";
import { EmptyState, ErrorState } from "@/components/states";
import { Button } from "@/components/ui/button";
import {
  useCastVote,
  useGuest,
  usePresence,
  useRevealCards,
  useRoom,
  useStartIssue,
  useStartNewRound,
} from "@/hooks/use-room";
import { useCountdownEnabled } from "@/hooks/use-preferences";
import { markLeaving, type Player, type RoomSnapshot } from "@/lib/api/rooms";
import { celebrate } from "@/lib/celebrate";
import {
  canManageIssues,
  canReveal,
  cardsFor,
  isAdmin,
  seatedPlayers,
  votesByPlayer,
} from "@/lib/poker";

export function RoomPage() {
  const { roomId = "" } = useParams();
  const guest = useGuest();
  const snapshot = useRoom(roomId, guest.isSuccess);

  // A failed background refetch keeps the room on screen; only a failed first load replaces it.
  const error = guest.error ?? (snapshot.data === undefined ? snapshot.error : null);
  if (error) {
    const retry = () => void (guest.isError ? guest.refetch() : snapshot.refetch());
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <ErrorState
          error={error}
          onRetry={retry}
          title="Could not open this room"
          testId="room-error"
          className="w-full max-w-md"
        />
      </div>
    );
  }

  if (!guest.data || snapshot.data === undefined) return <RoomSkeleton />;

  if (snapshot.data === null) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <EmptyState
          title="Room not found"
          description="The link may be mistyped, or the room no longer exists."
          testId="room-empty"
          className="w-full max-w-md"
          action={
            <Button asChild>
              <Link to="/create-new">
                <Plus /> Create a new room
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const me = snapshot.data.players.find((player) => player.user === guest.data);
  if (!me) return <JoinForm roomId={roomId} roomName={snapshot.data.room.name} />;

  return <RoomView snapshot={snapshot.data} userId={guest.data} me={me} />;
}

function RoomView({
  snapshot,
  userId,
  me,
}: {
  snapshot: RoomSnapshot;
  userId: string;
  me: Player;
}) {
  const { room, players, issues, votes } = snapshot;
  const [showInvite, setShowInvite] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [countingDown, setCountingDown] = useState(false);
  const countdownOn = useCountdownEnabled();
  const [reveals, setReveals] = useState(0);
  const [seenState, setSeenState] = useState(room.state);

  // Everyone in the room counts down together: the reveal arrives as a realtime state change.
  // Joining a room that is already revealed shows the cards without ceremony.
  if (room.state !== seenState) {
    setSeenState(room.state);
    const opened = room.state === "revealed";
    setCountingDown(opened && room.show_countdown);
    if (opened) setReveals((count) => count + 1);
  }
  const endCountdown = useCallback(() => setCountingDown(false), []);
  // Switching the countdown off in Settings (even mid-count) shows the cards straight away.
  const showCountdown = countingDown && countdownOn;

  usePresence(me.id);
  const castVote = useCastVote(room.id);
  const reveal = useRevealCards(room.id);
  const newRound = useStartNewRound(room.id);
  const startIssue = useStartIssue(room.id);

  const cards = votesByPlayer(votes);
  const myVote = castVote.isPending ? castVote.variables || undefined : cards.get(me.id);
  const revealed = room.state === "revealed" && !showCountdown;

  // Confetti the moment the cards turn over (after the countdown, if any).
  useEffect(() => {
    if (revealed && reveals > 0) celebrate();
  }, [revealed, reveals]);
  const seated = seatedPlayers(players);
  const currentIssue = issues.find((issue) => issue.id === room.current_issue);
  const mayReveal = canReveal(room, userId);

  return (
    <div className="flex h-dvh flex-col">
      <RoomHeader
        roomName={room.name}
        isAdmin={isAdmin(room, userId)}
        currentIssueTitle={currentIssue?.title}
        playerName={me.name}
        onLeave={() => markLeaving(me.id)}
        onInvite={() => setShowInvite(true)}
        onToggleIssues={() => setShowIssues((open) => !open)}
      />

      <div className="relative flex min-h-0 flex-1">
        {/* Wide screens only: a column of its own, so the ad never covers the table. */}
        <div className="hidden w-[332px] shrink-0 border-r border-border/50 p-4 xl:block">
          <AdSlot placement="room" />
        </div>
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-1 items-center justify-center overflow-y-auto p-4">
            <PokerTable
              players={seated}
              votes={cards}
              revealed={revealed}
              myPlayerId={me.id}
              canReveal={mayReveal}
              revealing={reveal.isPending || newRound.isPending || showCountdown}
              onReveal={() => reveal.mutate()}
              onNewRound={() => newRound.mutate()}
              onInvite={() => setShowInvite(true)}
            />
          </div>

          {revealed && (
            <ResultsPanel players={seated} votes={cards} showAverage={room.show_average} />
          )}

          {room.state === "voting" && (
            <CardSelector
              cards={cardsFor(room.voting_system)}
              selected={myVote}
              onSelect={(card) => castVote.mutate(card === myVote ? "" : card)}
            />
          )}
        </main>

        {showIssues && (
          <IssueSidebar
            roomId={room.id}
            issues={issues}
            currentIssueId={room.current_issue}
            canManage={canManageIssues(room, userId)}
            onSelect={(issue) => startIssue.mutate(issue.id)}
            onClose={() => setShowIssues(false)}
          />
        )}
      </div>

      <InviteDialog roomId={room.id} open={showInvite} onOpenChange={setShowInvite} />
      {showCountdown && <CountdownOverlay onComplete={endCountdown} />}
    </div>
  );
}
